import { CheckCircle2, Mail, Plus, RefreshCw, Shield, UserRound, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Badge, Section, WorkspaceSkeleton } from '../components/UI'
import { supabase } from '../lib/supabase'

type AuditConnection = {
  status: string
  tenant_id: string
  last_synced_at: string | null
  last_error: string | null
}

type Mailbox = {
  id: string
  provider: 'google' | 'microsoft'
  email_address: string
  status: string
  mailbox_type: 'personal' | 'shared'
  connected_user_id: string | null
  last_synced_at: string | null
  last_sync_status: string | null
  last_sync_error: string | null
}

export function Integrations() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [loading, setLoading] = useState(true)
  const [sharedOutlookEmail, setSharedOutlookEmail] = useState('')
  const [auditConnection, setAuditConnection] = useState<AuditConnection | null>(null)

  async function loadMailboxes() {
    if (!supabase) { setLoading(false); return }

    const [{ data, error }, auditResult] = await Promise.all([
      supabase
        .from('mailboxes')
        .select('id,provider,email_address,status,mailbox_type,connected_user_id,last_synced_at,last_sync_status,last_sync_error')
        .order('created_at', { ascending: true }),
      supabase
        .from('microsoft_audit_connections')
        .select('status,tenant_id,last_synced_at,last_error')
        .maybeSingle()
    ])

    if (error) setMessage(error.message)
    setMailboxes((data ?? []) as Mailbox[])
    if (!auditResult.error) setAuditConnection((auditResult.data ?? null) as AuditConnection | null)
    setLoading(false)
  }

  useEffect(() => {
    const connected = params.get('connected')
    const error = params.get('error')

    if (connected === 'microsoft-audit') setMessage('Advanced Microsoft Send As attribution is enabled for this organisation. Exchange audit events can now identify delegates when ordinary message metadata cannot.')
    else if (connected) setMessage(`${connected === 'google' ? 'Google' : 'Microsoft'} mailbox connected successfully. The permissions approval is complete unless access is later revoked.`)
    if (error) setMessage('The email connection could not be completed. Please try again.')

    loadMailboxes()
  }, [params])

  async function connect(provider: 'google' | 'microsoft', mailboxType: 'personal' | 'shared') {
    if (!supabase) {
      setMessage('Supabase is not configured.')
      return
    }

    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      nav('/login?returnTo=/app/integrations')
      return
    }

    const busyKey = `${provider}-${mailboxType}`
    setBusy(busyKey)
    setMessage('')

    const targetMailboxEmail = provider === 'microsoft' && mailboxType === 'shared' ? sharedOutlookEmail.trim() : undefined

    if (provider === 'microsoft' && mailboxType === 'shared' && !targetMailboxEmail) {
      setBusy(null)
      setMessage('Enter the shared Outlook mailbox address first.')
      return
    }

    const { data, error } = await supabase.functions.invoke(`${provider}-oauth-start`, {
      body: { mailboxType, returnTo: '/app/integrations', targetMailboxEmail }
    })

    setBusy(null)

    if (error || !data?.url) {
      setMessage(error?.message || data?.error || 'Could not start authorization.')
      return
    }

    window.location.assign(data.url)
  }


  async function enableMicrosoftAudit() {
    if (!supabase) return
    setBusy('microsoft-audit-consent')
    setMessage('')
    const { data, error } = await supabase.functions.invoke('microsoft-audit-consent-start', { body: {} })
    setBusy(null)
    if (error || !data?.url) {
      setMessage(data?.error || error?.message || 'Could not start Microsoft administrator consent.')
      return
    }
    window.location.assign(data.url)
  }

  async function refreshMicrosoftAudit() {
    if (!supabase) return
    setBusy('microsoft-audit-refresh')
    setMessage('')
    const { data, error } = await supabase.functions.invoke('microsoft-audit-attribution-sync', { body: {} })
    setBusy(null)
    if (error || data?.error || data?.status === 'error') {
      setMessage(data?.error || error?.message || 'Microsoft advanced attribution refresh failed.')
      await loadMailboxes()
      return
    }
    if (data?.status === 'pending') setMessage('Microsoft accepted the Exchange audit query. Attribution is still processing and will be completed on the next refresh.')
    else setMessage(`Advanced Microsoft attribution refreshed${typeof data?.attributed === 'number' ? ` · ${data.attributed} shared-mailbox repl${data.attributed === 1 ? 'y' : 'ies'} attributed` : ''}.`)
    await loadMailboxes()
  }

  async function refreshMetadata(mailbox: Mailbox) {
    if (!supabase) return
    setBusy(`metadata-${mailbox.id}`)
    setMessage('')

    const functionName = mailbox.provider === 'google' ? 'gmail-metadata-sync' : 'microsoft-metadata-sync'
    const { data, error } = await supabase.functions.invoke(functionName, { body: { mailboxId: mailbox.id } })
    setBusy(null)

    if (error || data?.error) {
      setMessage(data?.error || error?.message || `${mailbox.provider === 'google' ? 'Gmail' : 'Outlook'} metadata refresh failed.`)
      await loadMailboxes()
      return
    }

    const auditNote = mailbox.provider === 'microsoft' && data?.auditAttribution?.enabled
      ? data.auditAttribution.status === 'processed'
        ? ` Advanced audit attribution processed ${data.auditAttribution.attributed ?? 0} Send As/Send on Behalf repl${data.auditAttribution.attributed === 1 ? 'y' : 'ies'}.`
        : data.auditAttribution.status === 'pending' ? ' Microsoft is still processing the audit query; the next refresh will complete attribution.' : ''
      : ''
    setMessage(`Refreshed metadata for ${data.metadataRecords} ${mailbox.provider === 'google' ? 'Gmail' : 'Outlook'} messages. Shared-mailbox replies are attributed only when the provider exposes a reliable employee identity.${auditNote}`)
    await loadMailboxes()
  }

  if (loading) return <WorkspaceSkeleton rows={2}/>

  return <div className="stack-lg">
    <div className="intro-row">
      <div>
        <h2>Connect mailboxes once, then let Mail Percent keep measuring</h2>
        <p>Managers can connect their own mailbox or shared customer inboxes here. Employee personal inboxes are connected by each employee after they open the invitation link. Google or Microsoft shows the permissions screen during that first connection.</p>
      </div>
    </div>

    {message && <div className="notice"><CheckCircle2 size={20}/><div><strong>Connection status</strong><span>{message}</span></div></div>}

    <div className="integration-grid">
      <article className="integration-card">
        <div className="provider google"><img src="/images/integrations/gmail-logo.png" alt="Gmail" /></div>
        <div><h3>Google / Gmail</h3><p>Connect a mailbox using Google OAuth. For a shared Gmail account, authorise the shared account itself. Mail Percent can attribute delegated replies when Gmail exposes the delegate identity.</p></div>
        <div className="integration-action-stack">
          <button className="button primary" onClick={() => connect('google', 'personal')} disabled={busy === 'google-personal'}><UserRound size={16}/>{busy === 'google-personal' ? 'Opening Google…' : 'Connect my Gmail'}</button>
          <button className="button secondary" onClick={() => connect('google', 'shared')} disabled={busy === 'google-shared'}><Users size={16}/>{busy === 'google-shared' ? 'Opening Google…' : 'Connect shared Gmail'}</button>
        </div>
      </article>

      <article className="integration-card">
        <div className="provider microsoft"><img src="/images/integrations/outlook-logo.png" alt="Microsoft Outlook" /></div>
        <div><h3>Microsoft / Outlook</h3><p>Connect Microsoft 365 or Outlook through Microsoft OAuth. Shared mailboxes are authorised by a user with mailbox access; Send on Behalf replies can be attributed from Microsoft sender metadata.</p></div>
        <div className="integration-action-stack">
          <button className="button primary" onClick={() => connect('microsoft', 'personal')} disabled={busy === 'microsoft-personal'}><UserRound size={16}/>{busy === 'microsoft-personal' ? 'Opening Microsoft…' : 'Connect my Outlook'}</button>
          <details className="outlook-connect-disclosure">
            <summary className="button secondary"><Users size={16}/>Connect shared Outlook</summary>
          <div className="integration-shared-address">
            <label htmlFor="shared-outlook-email">Shared mailbox address</label>
            <input
              id="shared-outlook-email"
              type="email"
              value={sharedOutlookEmail}
              onChange={event => setSharedOutlookEmail(event.target.value)}
              placeholder="support@company.co.za"
            />
            <small>Sign in with a Microsoft 365 user that has Full Access. For complete reply reporting, Microsoft 365 should also save delegate-sent copies in the shared mailbox Sent Items.</small>
          </div>
          <button className="button secondary" onClick={() => connect('microsoft', 'shared')} disabled={busy === 'microsoft-shared'}><Users size={16}/>{busy === 'microsoft-shared' ? 'Opening Microsoft…' : 'Connect shared Outlook'}</button>
          </details>
          <details className="outlook-advanced-disclosure">
            <summary>Advanced Send As attribution</summary>
          <div className="integration-shared-address">
            <small>Optional. A Microsoft 365 tenant administrator grants Mail Percent Exchange audit access once so Send As replies can be matched to the delegate who actually sent them. Ordinary Outlook connections continue to work without this permission.</small>
            <div className="integration-action-stack">
              {auditConnection ? <>
                <Badge tone={auditConnection.status === 'connected' ? 'good' : 'bad'}>{auditConnection.status === 'connected' ? 'Tenant audit connected' : 'Tenant audit needs attention'}</Badge>
                <button className="button ghost" onClick={refreshMicrosoftAudit} disabled={busy === 'microsoft-audit-refresh'}><RefreshCw size={16}/>{busy === 'microsoft-audit-refresh' ? 'Checking audit logs…' : 'Refresh advanced attribution'}</button>
                {auditConnection.status !== 'connected' && <button className="button ghost" onClick={enableMicrosoftAudit} disabled={busy === 'microsoft-audit-consent'}><Shield size={16}/>{busy === 'microsoft-audit-consent' ? 'Opening Microsoft admin consent…' : 'Re-authorise tenant access'}</button>}
                {auditConnection.last_synced_at && <small>Last audit window processed {new Date(auditConnection.last_synced_at).toLocaleString()}.</small>}
                {auditConnection.last_error && <small>{auditConnection.last_error}</small>}
              </> : <>
                <button className="button ghost" onClick={enableMicrosoftAudit} disabled={busy === 'microsoft-audit-consent'}><Shield size={16}/>{busy === 'microsoft-audit-consent' ? 'Opening Microsoft admin consent…' : 'Enable advanced attribution'}</button>
              </>}
            </div>
          </div>
          </details>
        </div>
      </article>
    </div>

    <Section title="Connected customer email mailboxes" subtitle="Personal and shared mailbox connections available to this organisation">
      {mailboxes.length === 0 && <div className="connected-row"><div className="provider tiny"><Mail size={18}/></div><div><strong>No mailbox connected yet</strong><span>Connect your mailbox or a shared customer inbox above. Employees can connect their own inboxes from invitation links.</span></div><Badge tone="neutral">Waiting</Badge></div>}

      {mailboxes.map(mailbox => <div className="connected-row" key={mailbox.id}>
        <div className={`provider tiny ${mailbox.provider === 'google' ? 'google' : 'microsoft'}`}><img src={mailbox.provider === 'google' ? '/images/integrations/gmail-logo.png' : '/images/integrations/outlook-logo.png'} alt={mailbox.provider === 'google' ? 'Gmail' : 'Microsoft Outlook'} /></div>
        <div><strong>{mailbox.email_address}</strong><span>{mailbox.mailbox_type === 'shared' ? 'Shared team inbox' : 'Personal work inbox'}{mailbox.last_synced_at ? ` · Metadata refreshed ${new Date(mailbox.last_synced_at).toLocaleString()}` : ' · Connected'}{mailbox.last_sync_error ? ` · ${mailbox.last_sync_error}` : ''}</span></div>
        <Badge tone={mailbox.last_sync_status === 'error' ? 'bad' : mailbox.status === 'connected' ? 'good' : 'neutral'}>{mailbox.last_sync_status === 'error' ? 'Needs attention' : mailbox.status === 'connected' ? 'Connected' : mailbox.status}</Badge>
        <button className="button ghost" onClick={() => refreshMetadata(mailbox)} disabled={busy === `metadata-${mailbox.id}`}><RefreshCw size={16}/>{busy === `metadata-${mailbox.id}` ? 'Refreshing…' : 'Refresh metadata'}</button>
      </div>)}

      <button className="add-row" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}><Plus size={17}/>Connect another mailbox</button>
    </Section>

    <div className="notice"><Shield size={20}/><div><strong>Permissions are approved at connection time</strong><span>Each mailbox owner authorises Google or Microsoft once. Mail Percent then uses the provider API and stored refresh authorization to keep the connection running. Users only need to reconnect if access is revoked or the provider invalidates the connection.</span></div></div>
  </div>
}
